
import { NodeViewWrapper } from '@tiptap/react'
import React, { useEffect, useRef } from 'react'
import 'katex/dist/katex.min.css'
import katex from 'katex'
import s from './Component.module.css'
import { IconMath } from './IconMath'
import { useOutside } from '../../../hooks'
import cn from 'classnames'

const Tiptap = props => {
  const [ref, isShow, setIsShow] = useOutside(!(props.node.attrs.value || '').trim())
  const refInput = useRef()
  const refFormula = useRef()

  const getMathValue = node => {
    if (typeof node?.attrs?.value === 'string' && node.attrs.value.length > 0) {
      return node.attrs.value
    }

    return node?.content?.content?.map(item => item.text || '').join('') || ''
  }

  useEffect(() => {
    if (isShow && refInput.current) {
      refInput.current.focus()
    }
  }, [isShow])

  const increase = e => {
    props.updateAttributes({
      value: e.target.value,
    })
  }

  const paste = text => {
    const value = getMathValue(props.node)
    const input = refInput.current
    const start = input.selectionStart
    const end = input.selectionEnd
    const finText = value.substring(0, start) + text + value.substring(end)

    props.updateAttributes({
      value: finText,
    })
    input.focus()
    input.selectionEnd = start === end ? end + text.length : end
  }

  const formatString = text => {
    let newText = text.replace(/\n/g, '\\newline ').replace(/ /g, '\\,')
    const mathSymbolsRegex = /([−+=*:<>∩∪≤≥∈∥-])/g

    newText = newText.replace(mathSymbolsRegex, '\\text{$1}')
    newText = newText.replace(/\\cap/g, '~\\cap~')
    newText = newText.replace(/\\cup/g, '~\\cup~')
    newText = newText.replace(/\\leqslant/g, '~\\leqslant~')
    newText = newText.replace(/\\geqslant/g, '~\\geqslant~')
    newText = newText.replace(/\\parallel/g, '~\\parallel~')
    newText = newText.replace(/\\notin/g, '~\\notin~')
    newText = newText.replace(/\\in(?!\w)/g, '~\\in~')
    newText = newText.replace(/\\ge(?!\w)/g, '~\\ge~')
    newText = newText.replace(/\\le(?!\w)/g, '~\\le~')
    newText = newText.replace(/\\neq(?!\w)/g, '~\\neq~')
    newText = newText.replace(/\\equiv(?!\w)/g, '~\\equiv~')
    newText = newText.replace(/\\cdot/g, '~\\cdot~')
    newText = newText.replace(/%/g, '\\%')

    return newText
  }

  const renderFormula = formula => {
    const formattedFormula = formatString(formula)

    return katex.renderToString(formattedFormula, {
      throwOnError: false,
      displayMode: false,
    })
  }

  const setCaretAroundFormula = position => {
    if (typeof props.getPos !== 'function') {
      return
    }

    const pos = props.getPos()

    props.editor
      .chain()
      .focus()
      .setTextSelection(position === 'before' ? pos : pos + props.node.nodeSize)
      .run()
  }

  const handleMouseDown = e => {
    if (ref.current?.contains(e.target)) {
      return
    }

    const formulaElement = refFormula.current

    if (!formulaElement) {
      return
    }

    const rect = formulaElement.getBoundingClientRect()

    if (e.clientX < rect.left) {
      e.preventDefault()
      setIsShow(false)
      setCaretAroundFormula('before')
      return
    }

    if (e.clientX > rect.right) {
      e.preventDefault()
      setIsShow(false)
      setCaretAroundFormula('after')
      return
    }

    e.preventDefault()
    setIsShow(true)
  }

  return <NodeViewWrapper
    as="span"
    className={cn('react-component', s.mathWrapper)}
    onMouseDown={handleMouseDown}
  >
    <span
      ref={refFormula}
      className={cn('tiptap-math', s.mathLatex)}
      contentEditable={false}
      hint={getMathValue(props.node).trim() ? '' : 'введите функцию'}
      dangerouslySetInnerHTML={{ __html: renderFormula(getMathValue(props.node)) }}
    />
    {isShow && <span ref={ref} className={s.mathInputWrapper}>
      <textarea
        ref={refInput}
        className={s.mathInput}
        value={getMathValue(props.node)}
        onChange={increase}
        autoFocus={true}
      />
      <Keyboard onChange={paste} />
    </span>}
  </NodeViewWrapper>
}

const Keyboard = ({ onChange }) => {
  const buttons = ['sqrt', 'int', 'frac', 'infty', '^', 'sin', 'alpha', 'gamma', '_', 'cos', 'beta', 'pi']

  const onClick = type => {
    const add = text => onChange(text)

    switch (type) {
      case '^':
        add('^')
        break
      case '_':
        add('_')
        break
      case 'sqrt':
        add('\\sqrt[n]{из}')
        break
      case 'cos':
        add('\\cos')
        break
      case 'sin':
        add('\\sin')
        break
      case 'infty':
        add('\\infty')
        break
      case 'alpha':
        add('\\alpha')
        break
      case 'beta':
        add('\\beta')
        break
      case 'gamma':
        add('\\gamma')
        break
      case 'pi':
        add('\\pi')
        break
      case 'frac':
        add('\\frac{делимое}{делитель}')
        break
      case 'int':
        add('\\int_{от}^{до}')
        break
      default:
        break
    }
  }

  const link = () => window.open('https://ru.wikibooks.org/wiki/Математические_формулы_в_LaTeX#Ряды_и_интегралы')

  return <div className={s.mathKeyboard}>
    {buttons?.map(item => <ButtonKey key={item} type={item} onClick={onClick} />)}
    <button className={s.mathLink} onClick={link}>ещё</button>
  </div>
}

const ButtonKey = ({ type, onClick }) => {
  return <button onClick={() => onClick(type)}><IconMath type={type} /></button>
}

export default Tiptap
